/**
 * 알림센터(NotificationCenter 벨) 조회 + 읽음 처리 훅 (TanStack Query)
 *
 * - GET /notifications        알림 목록 (심각도 + 모듈 링크)  (계약 §1.3 범주)
 * - POST /notifications/{id}/read   읽음 처리 (있을 때만; 없으면 graceful)
 *
 * 동적 메타모델 원칙(절대):
 * - 모듈명 분기 금지. 알림의 typeCode/itemId 메타로 동적 라우팅.
 * - 심각도(긴급/주의/정보)는 NotificationSeverity 코드값. 표시는 i18n(컴포넌트 측).
 *
 * 엔드포인트 미구현(404 등) 시: graceful 처리(빈 목록/무동작).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api-client";
import type { SupportedLng } from "@/lib/i18n";
import type { NotificationSeverity } from "@eqms/shared-types";

/**
 * 알림 항목 (범용 — 모듈 하드코딩 금지).
 * 라우팅은 typeCode/itemId 메타로 결정.
 */
export interface NotificationItem {
  id: string;
  /** 알림 유형 코드 (규칙명) */
  type: string;
  /** 적재 시점 수신자 언어로 해석된 제목 — 옛 행·언어별 맵 없는 알림의 표시값. */
  title: string;
  /** 언어별 제목(ko/en/zh/ja). 없으면 `title` 을 그대로 씀. 표시는 `resolveLabel(title, titleI18n, lang)`. */
  titleI18n?: Record<string, string> | null;
  message: string;
  /** 언어별 본문. 없으면 `message` 를 그대로 씀. */
  messageI18n?: Record<string, string> | null;
  /** 심각도 (crit/warn/info) */
  severity?: NotificationSeverity;
  /** 연계 업무유형 코드 (모듈 링크) */
  typeCode?: string;
  /** 연계 아이템 id (모듈 링크) */
  itemId?: string;
  /** 읽음 시각 (UTC, null=미읽음) */
  readAt?: string | null;
  /** 생성 시각 (UTC, 서버 권위) */
  createdAt: string;
}

interface NotificationEnvelope {
  items?: NotificationItem[];
}

/** 알림 쿼리 키 (로컬 — query-keys.ts 미소유) */
function notificationsKey(workspaceSlug: string) {
  return ["workspace", workspaceSlug, "notifications"] as const;
}

/**
 * 현재 사용자 알림 목록.
 * 엔드포인트 부재(404) 시 빈 배열로 graceful 폴백.
 *
 * @param workspaceSlug 워크스페이스 컨텍스트(헤더)
 */
export function useNotifications(workspaceSlug: string | undefined) {
  return useQuery({
    queryKey: notificationsKey(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<NotificationItem[]> => {
      try {
        const res = await apiFetch<NotificationItem[] | NotificationEnvelope>(
          `/api/notifications`,
          { workspaceSlug },
        );
        if (Array.isArray(res)) return res;
        return res.items ?? [];
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return [];
        }
        throw err;
      }
    },
  });
}

/**
 * 알림 읽음 처리 뮤테이션 (POST /notifications/{id}/read).
 * 엔드포인트 부재 시 무동작(성공 처리) — 목록만 무효화.
 */
/**
 * 알림 발송 요청 — 수신자는 서버가 같은 회사 사용자인지 재검증한다(본문 ID를 그대로 믿지 않음).
 * 제목·본문은 4개 언어 맵(tAll 로 만듦)으로 보내고, 서버가 수신자마다 자기 언어(users.locale → en → ko)로 골라 적재한다.
 */
export interface NotificationSendRequest {
  targetUserIds: string[];
  titleI18n: Record<SupportedLng, string>;
  bodyI18n: Record<SupportedLng, string>;
  /** 관련 엔티티 종류(예: "ITEM") — 알림에서 원본으로 이동할 때 씀. */
  relatedEntityType?: string;
  relatedEntityId?: string;
}

/**
 * 인앱 알림 발송 — 단계 담당 지정·결재 요청 등 업무 흐름에서 지정된 사람에게 보냄.
 * 응답의 `sent` 는 **실제 적재된 건수**다(요청 인원수가 아니라) — 화면은 이 수를 그대로 안내함.
 */
export function useSendNotification(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotificationSendRequest) =>
      apiFetch<{ sent: number }>("/api/notifications", {
        method: "POST",
        body,
        workspaceSlug,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKey(workspaceSlug ?? "") });
    },
  });
}

export function useMarkNotificationRead(workspaceSlug: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      try {
        await apiFetch<void>(`/api/notifications/${encodeURIComponent(id)}/read`, {
          method: "POST",
          workspaceSlug,
        });
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return; // graceful — 엔드포인트 미구현
        }
        throw err;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationsKey(workspaceSlug ?? "") });
    },
  });
}
